import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  input,
  Signal,
  signal,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { SvgIconComponent } from 'angular-svg-icon';
import { sleep } from '@core/utils/sleep.util';
import { ToastrService } from 'ngx-toastr';
import { OverlayModule } from '@angular/cdk/overlay';
import { CountdownComponent } from 'ngx-countdown';
import { CaptchaOverlayComponent } from '@core/components/captcha-overlay/captcha-overlay.component';
import { SupplyDropState } from '@features/supply-drop/interfaces/supply-drop.interface';
import { Skin } from '@core/interfaces/skin.interface';
import { CostComponent } from '@core/components/cost/cost.component';
import { SupplyDropService } from '../supply-drop.service';
import { ModalService } from '@features/modals/services/modal.service';
import { CasesService } from '@features/cases/services/cases.service';
import { RewardsService } from '@features/rewards/services/rewards.service';
import { CaseReward } from '@features/rewards/interfaces/rewards.interface';

@Component({
  selector: 'app-supply-drop-widget',
  standalone: true,
  imports: [
    SvgIconComponent,
    OverlayModule,
    CaptchaOverlayComponent,
    CountdownComponent,
    CostComponent,
  ],
  templateUrl: './supply-drop-widget.component.html',
  styleUrl: './supply-drop-widget.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.rolling]': 'supplyDrop().status === "rolling"',
  },
})
export class SupplyDropWidgetComponent {
  supplyDrop = input.required<SupplyDropState>();
  avatars = signal<string[]>([]);
  skins = signal<Skin[]>([]);
  isRolling = signal<boolean>(false);

  wonIndex = signal<number | null>(null);

  rollWrapperAvatars = viewChild('rollWrapperAvatars', { read: ElementRef });
  rollContainerAvatars = viewChild('rollContainerAvatars', {
    read: ElementRef,
  });

  rollWrapperSkins = viewChild('rollWrapperSkins', { read: ElementRef });
  rollContainerSkins = viewChild('rollContainerSkins', { read: ElementRef });

  rollStatus = signal<'avatars' | 'skins'>('avatars');

  private animationFrameId = signal<number | null>(null);

  isCaptchaOverlay = false;
  private readonly DEFAULT_AVATARS_LENGTH = 100;

  showSkinsRollResults = signal<boolean>(false);
  showAvatarsRollResults = signal<boolean>(false);

  constructor(
    private toastrService: ToastrService,
    private supplyDropService: SupplyDropService,
    private modalService: ModalService,
    private casesService: CasesService,
    private rewardsService: RewardsService
  ) {
    afterRenderEffect(async () => {
      const rollWrapperAvatars = this.rollWrapperAvatars()?.nativeElement;
      if (this.supplyDrop().status === 'rolling' && rollWrapperAvatars) {
        const winningAvatar = Math.floor(Math.random() * 10) + 55;
        this.wonIndex.set(winningAvatar - 1);
        this.rollStatus.set('avatars');
        this.setAvatars(winningAvatar - 1);

        await this.roll(
          winningAvatar - 1,
          {
            wrapper: this.rollWrapperAvatars,
            container: this.rollContainerAvatars,
          },
          4000,
          this.showAvatarsRollResults
        );

        this.rollStatus.set('skins');
      }
    });
    afterRenderEffect(async () => {
      const rollWrapperSkins = this.rollWrapperSkins()?.nativeElement;
      if (this.supplyDrop().status === 'rolling' && rollWrapperSkins) {
        const winningSkin = Math.floor(Math.random() * 10) + 55;
        this.wonIndex.set(winningSkin - 1);

        this.setSkins(winningSkin - 1);
        await this.roll(
          winningSkin - 1,
          {
            wrapper: this.rollWrapperSkins,
            container: this.rollContainerSkins,
          },
          6000,
          this.showSkinsRollResults
        );

        this.closeSupplyDrop();
      }
    });
  }

  private closeSupplyDrop() {
    const dropData = this.supplyDrop().supplyDrop;
    if (dropData) {
      this.supplyDropService.postEndMessage(dropData);
    }

    this.supplyDropService.supplyDrop.update((prev) => ({
      status: 'none',
      captcha: prev.captcha,
      supplyDropInformation: null,
      supplyDrop: null,
      players: 0,
    }));
  }

  private setAvatars(winIndex: number) {
    const currentAvatars = this.supplyDrop().supplyDrop?.avatars ?? [];

    if (currentAvatars.length > 0) {
      const randomAvatars: string[] = [];
      for (let i = 0; i < this.DEFAULT_AVATARS_LENGTH; i++) {
        if (i === winIndex) {
          randomAvatars.push(
            this.supplyDrop().supplyDrop?.winner?.avatar ?? 'undefined winner'
          );
          continue;
        }

        const randomIndex = Math.floor(Math.random() * currentAvatars.length);
        randomAvatars.push(currentAvatars[randomIndex]);
      }
      this.avatars.set(randomAvatars);
    }
  }

  private setSkins(winIndex: number) {
    const dropData = this.supplyDrop().supplyDrop;
    if (!dropData) {
      console.error('No supply drop data available');
      return;
    }
    const currentSkins = dropData.case.skins;

    if (currentSkins.length > 0) {
      const randomSkins: Skin[] = [];
      for (let i = 0; i < this.DEFAULT_AVATARS_LENGTH; i++) {
        if (i === winIndex) {
          const winSkin = dropData.case.skins.find(
            (s) => s.name === dropData.result[0].skin
          );
          if (winSkin) {
            randomSkins.push(winSkin);
          } else {
            console.error('Winning skin not found in case skins');
            return;
          }

          continue;
        }

        const randomIndex = Math.floor(
          Math.random() * dropData.case.skins.length
        );
        randomSkins.push(dropData.case.skins[randomIndex]);
      }
      this.skins.set(randomSkins);
    }
  }

  private async roll(
    winIndex: number,
    elements: {
      wrapper: Signal<ElementRef<any> | undefined>;
      container: Signal<ElementRef<any> | undefined>;
    },
    resultsTimeoutDuration = 3500,
    setShowResults?: WritableSignal<boolean>
  ) {
    const rollWrapper = elements.wrapper()?.nativeElement;
    if (!rollWrapper) {
      console.error('rollWrapper not found');
      return;
    }

    const sizeCoefficient = 100 / this.DEFAULT_AVATARS_LENGTH;

    const middleLocation = -(sizeCoefficient * winIndex) - sizeCoefficient / 2;
    const transformX =
      -(sizeCoefficient * winIndex) -
      sizeCoefficient * (Math.random() * 0.6 + 0.2);
    await sleep(100);
    this.isRolling.set(true);
    rollWrapper.style.transition = `transform 6s cubic-bezier(0, 0.75, 0, 1)`;
    rollWrapper.style.transform = `translateX(${transformX}%)`;

    this.startAnimation(sizeCoefficient, {
      wrapper: elements.wrapper,
      container: elements.container,
    });

    await sleep(7000);

    rollWrapper.style.transition = `transform 0.3s ease-in-out`;
    rollWrapper.style.transform = `translateX(${middleLocation}%)`;
    this.stopAnimation();

    const itemElement = rollWrapper.children[winIndex];
    itemElement.classList.add('win');
    itemElement.classList.add('active');
    setShowResults?.set(true);
    this.isRolling.set(false);

    await sleep(resultsTimeoutDuration);
  }

  private startAnimation(
    size: number,
    elements: {
      wrapper: Signal<ElementRef<any> | undefined>;
      container: Signal<ElementRef<any> | undefined>;
    }
  ) {
    const rollContainer = elements.container()?.nativeElement;
    const rollWrapper = elements.wrapper()?.nativeElement;

    if (!rollContainer || !rollWrapper) {
      console.error(
        `${
          !rollContainer && !rollWrapper
            ? 'rollContainer and rollWrapper'
            : !rollContainer
            ? 'rollContainer'
            : !rollWrapper
            ? 'rollWrapper'
            : ''
        } not found`
      );
      return;
    }

    const animate = () => {
      const containerRect = rollContainer.getBoundingClientRect();
      const rollRect = rollWrapper.getBoundingClientRect();
      const rollCenter = containerRect.left + containerRect.width / 2;

      const transformPercentage =
        (Math.abs(rollRect.left - rollCenter) / rollRect.width) * 100;
      const currentRoll = Math.floor(transformPercentage / size);

      if (currentRoll >= 0 && currentRoll < rollWrapper.children.length) {
        this.updateActiveItem(currentRoll, { wrapper: elements.wrapper });
      }

      this.animationFrameId.set(requestAnimationFrame(animate));
    };

    this.animationFrameId.set(requestAnimationFrame(animate));
  }

  private stopAnimation() {
    const frameId = this.animationFrameId();
    if (frameId !== null) {
      cancelAnimationFrame(frameId);
      this.animationFrameId.set(null);
    }
  }

  private updateActiveItem(
    index: number,
    elements: { wrapper: Signal<ElementRef<any> | undefined> }
  ) {
    const rollElement = elements.wrapper()?.nativeElement;
    if (!rollElement) {
      console.error('rollElement not found');
      return;
    }

    const currentActive = rollElement.querySelector('.active');
    const newActive = rollElement.children[index];

    if (currentActive !== newActive) {
      if (currentActive) {
        currentActive.classList.remove('active');
      }
      if (newActive && !newActive.classList.contains('active')) {
        newActive.classList.add('active');
      }
    }
  }

  public openCaseSkinsModal() {
    const rewardCase = this.rewardsService.rewardsWithProgress()
      .find((r): r is CaseReward => r.type === 'case' && r.reward.link.toLowerCase() === 'bronze-v');

    const caseItem = rewardCase?.reward ?? this.casesService.findCaseByLink('bronze-v');

    if (caseItem) {
      this.modalService.openModal('CaseBattlesCaseSkinsModalComponent', {
        caseItem,
        balance: 'skins',
        showPrice: false,
        responsiveImage: true,
      });
    }
  }

  public async handleVerifyCaptcha(captcha: string) {
    const message = await this.supplyDropService.joinSupplyDrop(captcha);
    this.toastrService.success(message);
    this.isCaptchaOverlay = false;
  }

  public openOverlay() {
    if (!this.supplyDrop().captcha) {
      this.toastrService.error('Captcha loading error, try login');
      return;
    }

    this.isCaptchaOverlay = true;
  }
}
